import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { usePreferenceStore } from '@/lib/store/preferenceStore';
import Card from '@/components/common/Card';
import Button from '@/components/common/Button';
import Select, { SelectOption } from '@/components/common/Select';
import Input from '@/components/common/Input';
import { Switch } from '@/components/common/Switch';
import Alert from '@/components/common/Alert';

const schedulingPreferencesSchema = z.object({
  auto_publish_schedule: z.boolean(),
  provider_preference_deadline_days: z.number()
    .int()
    .min(1, { message: "Deadline must be at least 1 day" })
    .max(30, { message: "Deadline cannot exceed 30 days" }),
  weekend_distribution_strategy: z.enum(['equal', 'preference_based', 'seniority_based']),
  honor_request_days_off_priority: z.enum(['high', 'medium', 'low']),
  enforce_minimum_rest_period: z.boolean(),
  enable_night_shift_rotation: z.boolean()
});

type SchedulingPreferencesFormValues = z.infer<typeof schedulingPreferencesSchema>;

// Define select options
const weekendDistributionOptions: SelectOption[] = [
  { value: 'equal', label: 'Equal Distribution' },
  { value: 'preference_based', label: 'Preference-Based' },
  { value: 'seniority_based', label: 'Seniority-Based' }
];

const requestDaysOffPriorityOptions: SelectOption[] = [
  { value: 'high', label: 'High - Honor whenever possible' },
  { value: 'medium', label: 'Medium - Balance with other factors' },
  { value: 'low', label: 'Low - Only if doesn\'t affect staffing' }
];

export const SchedulingPreferencesForm = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { teamPreferences, isLoading, error, fetchTeamPreferences, updateSchedulingPreferences } = usePreferenceStore();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<SchedulingPreferencesFormValues>({
    resolver: zodResolver(schedulingPreferencesSchema),
    defaultValues: {
      auto_publish_schedule: false,
      provider_preference_deadline_days: 14,
      weekend_distribution_strategy: 'equal',
      honor_request_days_off_priority: 'high',
      enforce_minimum_rest_period: true,
      enable_night_shift_rotation: false
    }
  });
  
  // For toggling switches
  const autoPublish = watch('auto_publish_schedule');
  const enforceMinRest = watch('enforce_minimum_rest_period');
  const enableNightRotation = watch('enable_night_shift_rotation');
  const weekendStrategy = watch('weekend_distribution_strategy');
  const daysOffPriority = watch('honor_request_days_off_priority');
  
  useEffect(() => {
    if (teamId) {
      fetchTeamPreferences(teamId).then(preferences => {
        if (preferences?.scheduling_preferences) {
          const sp = preferences.scheduling_preferences;
          setValue('auto_publish_schedule', sp.auto_publish_schedule);
          setValue('provider_preference_deadline_days', sp.provider_preference_deadline_days);
          setValue('weekend_distribution_strategy', sp.weekend_distribution_strategy);
          setValue('honor_request_days_off_priority', sp.honor_request_days_off_priority);
          setValue('enforce_minimum_rest_period', sp.enforce_minimum_rest_period);
          setValue('enable_night_shift_rotation', sp.enable_night_shift_rotation);
        }
      });
    }
  }, [teamId, fetchTeamPreferences, setValue]);
  
  const onSubmit = async (data: SchedulingPreferencesFormValues) => {
    if (!teamId) return;
    
    setIsSubmitting(true);
    setSuccessMessage(null);
    
    try {
      await updateSchedulingPreferences(teamId, data);
      setSuccessMessage('Scheduling preferences updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Failed to update preferences:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading && !teamPreferences) {
    return (
      <Card className="p-6">
        <div className="flex justify-center py-8">
          <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full"></div>
        </div>
      </Card>
    );
  }
  
  return (
    <Card className="p-6 shadow-sm">
      <h2 className="text-xl font-semibold mb-6 text-gray-800">Scheduling Preferences</h2>
      
      {error && (
        <Alert variant="error" className="mb-6">
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert variant="success" className="mb-6">
          {successMessage}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-6">
          <div className="flex items-center bg-gray-50 p-5 rounded-lg min-h-[100px]">
            <div className="flex-grow mr-6">
              <h3 className="text-base font-medium text-gray-800">Auto-Publish Schedule</h3>
              <p className="text-sm text-gray-500 mt-1">
                Automatically publish schedules once generated
              </p>
            </div>
            <div className="flex-shrink-0 w-20">
              <Switch
                checked={autoPublish}
                onChange={() => setValue('auto_publish_schedule', !autoPublish)}
              />
            </div>
          </div>
          
          <div className="bg-gray-50 p-5 rounded-lg">
            <Input
              type="number"
              label="Provider Preference Deadline (Days Before Schedule)"
              {...register('provider_preference_deadline_days', { valueAsNumber: true })}
              error={errors.provider_preference_deadline_days?.message}
              helperText="Number of days before schedule start that provider preferences are due"
              required
              fullWidth
            />
          </div>
          
          <div className="bg-gray-50 p-5 rounded-lg">
            <Select
              label="Weekend Distribution Strategy"
              options={weekendDistributionOptions}
              value={weekendStrategy}
              onChange={(e) => setValue('weekend_distribution_strategy', e.target.value as 'equal' | 'preference_based' | 'seniority_based')}
              error={errors.weekend_distribution_strategy?.message}
              required
              fullWidth
            />
          </div>
          
          <div className="bg-gray-50 p-5 rounded-lg">
            <Select
              label="Request Days Off Priority"
              options={requestDaysOffPriorityOptions}
              value={daysOffPriority}
              onChange={(e) => setValue('honor_request_days_off_priority', e.target.value as 'high' | 'medium' | 'low')}
              error={errors.honor_request_days_off_priority?.message}
              helperText="Priority level for honoring provider request days off"
              required
              fullWidth
            />
          </div>
          
          <div className="flex items-center bg-gray-50 p-5 rounded-lg min-h-[100px]">
            <div className="flex-grow mr-6">
              <h3 className="text-base font-medium text-gray-800">Enforce Minimum Rest Period</h3>
              <p className="text-sm text-gray-500 mt-1">
                Ensure providers have required rest between shifts
              </p>
            </div>
            <div className="flex-shrink-0 w-20">
              <Switch
                checked={enforceMinRest}
                onChange={() => setValue('enforce_minimum_rest_period', !enforceMinRest)}
              />
            </div>
          </div>
          
          <div className="flex items-center bg-gray-50 p-5 rounded-lg min-h-[100px]">
            <div className="flex-grow mr-6">
              <h3 className="text-base font-medium text-gray-800">Enable Night Shift Rotation</h3>
              <p className="text-sm text-gray-500 mt-1">
                Rotate night shifts among eligible providers
              </p>
            </div>
            <div className="flex-shrink-0 w-20">
              <Switch
                checked={enableNightRotation}
                onChange={() => setValue('enable_night_shift_rotation', !enableNightRotation)}
              />
            </div>
          </div>
        </div>
        
        <div className="pt-6 border-t mt-6 flex justify-end">
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
            size="lg"
          >
            Save Preferences
          </Button>
        </div>
      </form>
    </Card>
  );
}; 